import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import DeviceRouter from "./src/pages/DeviceRouter";
import Today from "./src/pages/Today";
import Web_Base from "./src/pages/Web_Base";
import Dashboard from "./src/pages/Dashboard";
import Login from "./src/pages/Login";
import Photo from "./src/pages/Photo";
import Messages from "./src/pages/Messages";
import GmailInbox from "./src/pages/GmailInbox";
import RoutePlanner from "./src/pages/RoutePlanner";
import MapViewer from "./src/pages/MapViewer";
import Locks from "./src/pages/Locks";
import CustomerLocks from "./src/pages/CustomerLocks";
import Calendar from "./src/pages/Calendar";
import Settings from "./src/pages/Settings";
import UserSettings from "./src/pages/UserSettings";
import Admin from "./src/pages/Admin";
import NewBooking from "./src/pages/NewBooking";
import BookingWizard from "./src/pages/BookingWizard";
import CashPayment from "./src/pages/CashPayment";
import Waiver from "./src/pages/Waiver";
import StripeCheckout from "./src/pages/StripeCheckout";
import PaymentSuccess from "./src/pages/PaymentSuccess";
import Bookings from "./src/pages/Bookings";
import ViewBooking from "./src/pages/ViewBooking";
import ViewWaivers from "./src/pages/ViewWaivers";
import EditBooking from "./src/pages/EditBooking";
import EditBookingWizard from "./src/pages/EditBookingWizard";
import DirectEditBooking from "./src/pages/DirectEditBooking";
import CheckIn from "./src/pages/CheckIn";
import ResetBookingPin from "./src/pages/ResetBookingPin";
import CancelBookingConfirmation from "./src/pages/CancelBookingConfirmation";
import TransferWaiverPage from "./src/pages/TransferWaiverPage";
import AllMedia from "./src/pages/AllMedia";
import ContactPad from "./src/pages/ContactPad";
import PrivacyPolicy from "./src/pages/PrivacyPolicy";
import TermsConditions from "./src/pages/TermsConditions";
import ProblemReports from "./src/pages/ProblemReports";
import SubmitBugReport from "./src/pages/SubmitBugReport";
import ViewBugReports from "./src/pages/ViewBugReports";
import BugReport from "./src/pages/BugReport";
import MobileSimulator from "./src/components/MobileSimulator";
import ErrorBoundary from "./src/components/ErrorBoundary";
import { initializeLocksCache } from "./src/services/locksCache";
import { initializeBookingsCache } from "./src/services/bookingsCache";
import { initializePendingBookingsCache } from "./src/services/pendingBookingsCache";
import { initializeProductsCache } from "./src/services/productsCache";
import { initializeCustomersCache } from "./src/services/customersCache";
import { initializeUserStoreCache, clearUserStoreCache } from "./src/services/userStoreCache";
import { initializeGmailFoldersCache } from "./src/services/gmailFoldersCache";
import { initializeSettingsCache, clearSettingsCache } from "./src/services/settingsCache";
import { checkAndClearCache } from "./src/services/cacheManager";
import { initializeNetworkMonitor } from "./src/services/networkStatus";
import {
  parseBookingEmailV3Local,
  ENDING_ADDRESS,
} from "./src/utils/bookingEmailParser";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "./src/firebase.config";
import { onAuthStateChanged } from "firebase/auth";

import { initializeMessagesListener } from "./src/services/messagesListener";
import { initializeUsersCache } from "./src/services/usersCache";
import { useAuthStore } from "./src/stores";
import { listenToCallExpectations } from "./src/db_calls_wrapper";
import CallbackBanner from "./src/components/CallbackBanner";
import SwitchUserOverlay from "./src/components/SwitchUserOverlay";
import {
  setupForegroundListener,
  registerForNotifications,
  areNotificationsEnabled,
} from "./src/services/fcmService";

// Main App Component with Router
const App = () => {
  const { isAuthenticated, authChecked, setUser, setAuthChecked, setIsAdmin } = useAuthStore();

  // Redirect components for backward compatibility
  const BookingRedirect = () => {
    const { bookingId } = useParams();
    return <Navigate to={`/view-booking/${bookingId}`} replace />;
  };

  const CancelBookingRedirect = () => {
    const { bookingId } = useParams();
    return <Navigate to={`/cancel-booking-confirmation/${bookingId}`} replace />;
  };

  const TransferWaiverRedirect = () => {
    const { bookingId } = useParams();
    return <Navigate to={`/transfer-waiver-page/${bookingId}`} replace />;
  };

  // Initialize network monitoring
  useEffect(() => {
    const cleanup = initializeNetworkMonitor();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Keep screen awake on mobile devices (for development)
  useEffect(() => {
    // Only activate on mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) return;

    let wakeLock = null;
    let handleVisibilityChange = null;
    let wakeLockInterval = null;
    let isActive = true;
    let retryCount = 0;
    const MAX_RETRIES = 5;
    
    const requestWakeLock = async () => {
      if (!isActive) return;
      
      // Check if Wake Lock API is supported
      if (!('wakeLock' in navigator)) {
        console.log('ℹ️ Wake Lock API not supported in this browser');
        console.log('💡 Use Chrome, Edge, or Safari 16.4+ on mobile for wake lock support');
        return;
      }
      
      try {
        // Release any existing lock first
        if (wakeLock && !wakeLock.released) {
          try {
            await wakeLock.release();
          } catch (e) {
            // Ignore errors releasing old lock
          }
        }
        
        wakeLock = await navigator.wakeLock.request('screen');
        console.log('✅ Screen wake lock activated');
        retryCount = 0; // Reset retry count on success
        
        // Listen for release events
        const releaseHandler = () => {
          console.log('⚠️ Wake lock was released by browser');
          wakeLock = null;
          // Try to re-acquire immediately if still active
          if (isActive && document.visibilityState === 'visible' && retryCount < MAX_RETRIES) {
            setTimeout(() => requestWakeLock(), 500);
          }
        };
        
        wakeLock.addEventListener('release', releaseHandler);
        
        // Re-acquire wake lock if it's released (e.g., when tab becomes visible again)
        handleVisibilityChange = async () => {
          if (!isActive) return;
          
          if (document.visibilityState === 'visible') {
            try {
              // Check if lock is still active
              if (!wakeLock || wakeLock.released) {
                console.log('🔄 Re-acquiring wake lock after visibility change...');
                retryCount = 0; // Reset on visibility change
                wakeLock = await navigator.wakeLock.request('screen');
                console.log('✅ Screen wake lock re-acquired');
                
                // Re-attach release listener
                wakeLock.addEventListener('release', releaseHandler);
              }
            } catch (err) {
              console.warn('⚠️ Could not re-acquire wake lock:', err);
              retryCount++;
              // Retry after a delay, but limit retries
              if (isActive && retryCount < MAX_RETRIES) {
                setTimeout(() => requestWakeLock(), 2000);
              }
            }
          }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Aggressively check and re-acquire wake lock (every 5 seconds)
        wakeLockInterval = setInterval(async () => {
          if (!isActive) return;
          
          try {
            if (!wakeLock || wakeLock.released) {
              console.log('🔄 Wake lock was released, re-acquiring...');
              retryCount = 0; // Reset on interval check
              wakeLock = await navigator.wakeLock.request('screen');
              console.log('✅ Screen wake lock re-acquired via interval');
              
              // Re-attach release listener
              wakeLock.addEventListener('release', releaseHandler);
            }
          } catch (err) {
            console.warn('⚠️ Could not re-acquire wake lock via interval:', err);
            retryCount++;
            // Retry after a delay, but limit retries
            if (isActive && retryCount < MAX_RETRIES) {
              setTimeout(() => requestWakeLock(), 2000);
            } else if (retryCount >= MAX_RETRIES) {
              console.error('❌ Max retries reached for wake lock. Please refresh the page.');
            }
          }
        }, 5000); // Check every 5 seconds
      } catch (err) {
        console.warn('⚠️ Could not acquire wake lock:', err);
        retryCount++;
        // Retry after a delay, but limit retries
        if (isActive && retryCount < MAX_RETRIES) {
          setTimeout(() => requestWakeLock(), 2000);
        } else {
          console.error('❌ Failed to acquire wake lock after retries. Error:', err.message);
          console.log('💡 Wake lock may require user interaction. Try tapping the screen.');
        }
      }
    };
    
    // Try initial request immediately
    requestWakeLock();
    
    // Also try to acquire on ANY user interaction (some browsers require this)
    const handleUserInteraction = () => {
      if (isActive && (!wakeLock || (wakeLock && wakeLock.released))) {
        retryCount = 0; // Reset on user interaction
        requestWakeLock();
      }
    };
    
    // Listen for user interactions to acquire/re-acquire
    const events = ['touchstart', 'click', 'touchend', 'mousedown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: false, passive: true });
    });
    
    // Cleanup on unmount
    return () => {
      isActive = false;
      
      if (wakeLockInterval) {
        clearInterval(wakeLockInterval);
      }
      if (handleVisibilityChange) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
      
      if (wakeLock && !wakeLock.released) {
        wakeLock.release().then(() => {
          console.log('✅ Screen wake lock released');
        }).catch((err) => {
          console.warn('⚠️ Error releasing wake lock:', err);
        });
      }
    };
  }, []);
  
  // Initialize users cache on app load (before auth) using default tenant/store path
  useEffect(() => {
    console.log('👥 Initializing users cache on app load (pre-auth)');
    const unsubscribeUsers = initializeUsersCache(true); // Use default path
    
    return () => {
      if (unsubscribeUsers) unsubscribeUsers();
    };
  }, []);

  // Monitor auth state for persistence and load user store
  useEffect(() => {
    let unsubscribeUserStore = null;
    let cacheUnsubscribers = null;
    let preAuthUsersUnsubscribe = null;

    const initializeAllCaches = () => {
      const unsubscribeLocks = initializeLocksCache();
      const unsubscribeBookings = initializeBookingsCache();
      // Feeds soft inventory holds during checkout - see rentalAvailability.js
      const unsubscribePendingBookings = initializePendingBookingsCache();
      const unsubscribeCustomers = initializeCustomersCache();
      const productsUnsubscribers = initializeProductsCache();
      const unsubscribeGmailFolders = initializeGmailFoldersCache();
      const unsubscribeSettings = initializeSettingsCache();
      const unsubscribeMessages = initializeMessagesListener();
      // Use tenant/store context for authenticated users
      const unsubscribeUsers = initializeUsersCache(false);
      const unsubscribeCallExpectations = listenToCallExpectations();

      return {
        unsubscribeLocks,
        unsubscribeBookings,
        unsubscribePendingBookings,
        unsubscribeCustomers,
        unsubscribeGmailFolders,
        unsubscribeSettings,
        unsubscribeMessages,
        unsubscribeUsers,
        unsubscribeCallExpectations,
        ...productsUnsubscribers
      };
    };

    const cleanupAllCaches = (unsubscribers) => {
      if (!unsubscribers) return;
      if (unsubscribers.unsubscribeLocks) unsubscribers.unsubscribeLocks();
      if (unsubscribers.unsubscribeBookings) unsubscribers.unsubscribeBookings();
      if (unsubscribers.unsubscribePendingBookings) unsubscribers.unsubscribePendingBookings();
      if (unsubscribers.unsubscribeCustomers) unsubscribers.unsubscribeCustomers();
      if (unsubscribers.unsubscribeGmailFolders) unsubscribers.unsubscribeGmailFolders();
      if (unsubscribers.unsubscribeSettings) unsubscribers.unsubscribeSettings();
      if (unsubscribers.unsubscribeMessages) unsubscribers.unsubscribeMessages();
      if (unsubscribers.unsubscribeUsers) unsubscribers.unsubscribeUsers();
      if (unsubscribers.unsubscribeCallExpectations) unsubscribers.unsubscribeCallExpectations();
      if (unsubscribers.unsubscribeProducts1) unsubscribers.unsubscribeProducts1();
      if (unsubscribers.unsubscribeProducts2) unsubscribers.unsubscribeProducts2();
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Initialize user store cache with real-time listener
        unsubscribeUserStore = initializeUserStoreCache(user.uid);
        
        // IMPORTANT: Wait a moment for user store to populate before initializing other caches
        // This ensures getTenantId() and getStoreId() have values
        setTimeout(() => {
          cacheUnsubscribers = initializeAllCaches();
        }, 500); // Wait 500ms for user store to load
        
        setUser(user);
        
        // Check for admin claims
        try {
          const idTokenResult = await user.getIdTokenResult();
          setIsAdmin(!!idTokenResult.claims.admin);
        } catch (error) {
          console.error("Error checking admin claims:", error);
          setIsAdmin(false);
        }
      } else {
        // DEV AUTO-LOGIN DISABLED: fall through to normal login flow

        // Clean up any existing cache listeners from a previous authenticated session
        if (cacheUnsubscribers) {
          cleanupAllCaches(cacheUnsubscribers);
          cacheUnsubscribers = null;
        }

        // Clear user store cache on logout
        clearUserStoreCache();
        clearSettingsCache();
        setUser(null);
        setIsAdmin(false);
        
        // Clean up user store listener if it exists
        if (unsubscribeUserStore) {
          unsubscribeUserStore();
          unsubscribeUserStore = null;
        }

        // Initialize all caches for unauthenticated users as well (using default tenant/store context)
        // getCollectionPath/getSettingsPath will fall back to defaults when no user is logged in
        cacheUnsubscribers = initializeAllCaches();
      }
      setAuthChecked(true);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserStore) {
        unsubscribeUserStore();
      }
      cleanupAllCaches(cacheUnsubscribers);
    };
  }, [setUser, setIsAdmin, setAuthChecked]);

  // Setup FCM foreground message listener and service worker notification click handler
  useEffect(() => {
    if (!isAuthenticated) return;

    // Setup foreground message listener
    const unsubscribe = setupForegroundListener((payload) => {
      console.log('📨 Notification received in foreground:', payload);
      // Additional handling can be added here if needed
      // For example, showing an in-app notification, updating a badge count, etc.
    });

    // Re-register this device's token on every launch once permission exists.
    // FCM tokens rotate, and a rotated token means the server keeps pushing to
    // a dead address with no visible error. getToken() does not prompt when
    // permission is already granted, so this needs no user gesture.
    if (areNotificationsEnabled()) {
      registerForNotifications().catch((error) =>
        console.warn('⚠️ Token refresh failed:', error)
      );
    }

    // Listen for messages from the service worker (notification clicks).
    //
    // The FCM SDK's own notificationclick handler focuses an already-open
    // window without navigating it -- it only postMessages the payload -- so
    // this is what actually moves a warm app to the right conversation.
    const handleServiceWorkerMessage = (event) => {
      const payload = event.data;
      if (!payload) return;

      const isNotificationClick =
        (payload.isFirebaseMessaging &&
          payload.messageType === 'notification-clicked') ||
        payload.type === 'NOTIFICATION_CLICK';

      if (!isNotificationClick) return;

      const url = payload.data?.url || payload.fcmOptions?.link || payload.url;
      if (!url) return;

      // Already on the target thread (the common case when the tap just
      // brings the app forward) -- a reload would only cost time.
      const current = `${window.location.pathname}${window.location.search}`;
      if (current === url) return;

      console.log('🔔 Notification clicked, navigating to:', url);
      window.location.href = url;
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      if (unsubscribe) unsubscribe();
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [isAuthenticated]);

  // Check version and clear stale cache on app load
  useEffect(() => {
    // Check version IMMEDIATELY on app load (before everything else)
    // This ensures users always get the latest code
    // NOTE: Cache initialization moved to auth useEffect to ensure tenant/store context is available
    checkAndClearCache()
      .catch((error) => {
        console.error("❌ Error during version check:", error);
      });
  }, []);

  // Show nothing until auth is checked
  if (!authChecked) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007AFF',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
      </div>
    );
  }

  const DEV_MODE = process.env.NODE_ENV === "development";

  // Protected route wrapper
  const ProtectedRoute = ({ children }) => {
    // In production, redirect to login if not authenticated
    // Exception: public pages like /booking/:id, /edit-booking/:id, /cancel-booking/:id, /reset-pin, /transfer-waiver/:id don't need auth
  if (!isAuthenticated && !DEV_MODE) {
      const publicPaths = [
        '/',                 // Base domain should show rental bookings for everyone
        '/rental-bookings',  // Public booking flow
        '/booking/',         // Old route (redirects to /view-booking/)
        '/view-booking/',    // New route
        '/edit-booking/',
        '/cancel-booking/',  // Old route (redirects to /cancel-booking-confirmation/)
        '/cancel-booking-confirmation/',  // New route
        '/reset-pin',        // Old route (redirects to /reset-booking-pin)
        '/reset-booking-pin', // New route
        '/transfer-waiver/', // Old route (redirects to /transfer-waiver-page/)
        '/transfer-waiver-page/', // New route
        '/login',
        '/payment-success',
        '/stripe-checkout',
        '/privacy-policy',
        '/terms-conditions'
      ];
      const isPublicPath = publicPaths.some(path => window.location.pathname.startsWith(path));
      
      if (!isPublicPath) {
        return <Navigate to="/login" replace />;
  }
    }
    return children;
  };

  return (
    <ErrorBoundary>
    <Router>
      {isAuthenticated && <CallbackBanner />}
      {/* Renders null unless a user switch is in flight or its greeting toast is
          still up, so it can sit above every route without gating. */}
      <SwitchUserOverlay />
      <Routes>
        {/* Default route - Conditional based on auth */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {isAuthenticated ? (
                <MobileSimulator>
                  <Dashboard />
                </MobileSimulator>
              ) : (
                <MobileSimulator>
                  <NewBooking />
                </MobileSimulator>
              )}
            </ProtectedRoute>
          }
        />

        {/* Device detection and routing */}
        <Route path="/device" element={<DeviceRouter />} />

        {/* Mobile routes - wrapped in simulator for desktop development */}
        <Route
          path="/today"
          element={
            <MobileSimulator>
              <Today />
            </MobileSimulator>
          }
        />
        <Route
          path="/photo"
          element={
            <MobileSimulator>
              <Photo />
            </MobileSimulator>
          }
        />
        <Route
          path="/messages"
          element={
            <MobileSimulator>
              <Messages />
            </MobileSimulator>
          }
        />
        <Route
          path="/gmail-inbox"
          element={<GmailInbox />}
        />
        <Route
          path="/route-planner"
          element={
            <MobileSimulator>
              <RoutePlanner />
            </MobileSimulator>
          }
        />
        <Route
          path="/locks"
          element={
            <MobileSimulator>
              <Locks />
            </MobileSimulator>
          }
        />
        <Route
          path="/map-viewer"
          element={
            <MobileSimulator>
              <MapViewer />
            </MobileSimulator>
          }
        />
        <Route
          path="/customer-locks"
          element={
            <MobileSimulator>
              <CustomerLocks />
            </MobileSimulator>
          }
        />
        <Route
          path="/calendar"
          element={
            <MobileSimulator>
              <Calendar />
            </MobileSimulator>
          }
        />

        {/* Desktop routes */}
        <Route path="/web-base" element={<Web_Base />} />
        <Route path="/locks-desktop" element={<Locks />} />
          <Route path="/contact-pad" element={<ContactPad />} />
          <Route path="/contact-pad/:phoneNumber" element={<ContactPad />} />

        {/* Rental Booking routes - wrapped in simulator for desktop development */}
        <Route
          path="/new-booking"
          element={
            <MobileSimulator>
              <NewBooking />
            </MobileSimulator>
          }
        />
        {/* Cash desk. Staff only, and deliberately NOT in publicPaths — this screen
            marks a booking paid. */}
        <Route
          path="/cash-payment/:bookingId"
          element={
            <ProtectedRoute>
              <MobileSimulator>
                <CashPayment />
              </MobileSimulator>
            </ProtectedRoute>
          }
        />
        {/* Public: this is the customer-facing booking page the website's
            "Rent Now" buttons link to. Staff get extra controls inside the
            wizard (rate override, booking modes) gated on isAuthenticated. */}
        <Route
          path="/booking-wizard"
          element={
            <MobileSimulator>
              <BookingWizard />
            </MobileSimulator>
          }
        />
        <Route
          path="/settings"
          element={
            <MobileSimulator>
              <Settings />
            </MobileSimulator>
          }
        />
        <Route
          path="/user-settings"
          element={
            <MobileSimulator>
              <UserSettings />
            </MobileSimulator>
          }
        />
        <Route
          path="/admin"
          element={
            <MobileSimulator>
              <Admin />
            </MobileSimulator>
          }
        />
        <Route
          path="/waiver"
          element={
            <MobileSimulator>
              <Waiver />
            </MobileSimulator>
          }
        />
        <Route
          path="/stripe-checkout"
          element={
            <MobileSimulator>
              <StripeCheckout />
            </MobileSimulator>
          }
        />
        <Route
          path="/payment-success"
          element={
            <MobileSimulator>
              <PaymentSuccess />
            </MobileSimulator>
          }
        />
        <Route
          path="/bookings"
          element={
            <MobileSimulator>
              <Bookings />
            </MobileSimulator>
          }
        />
        <Route path="/view-booking/:bookingId" element={<ViewBooking />} />
        {/* Staff only — deliberately NOT in publicPaths, so logged-out visitors
            are redirected to /login by ProtectedRoute. Without an id the wizard
            shows a booking picker. */}
        <Route
          path="/edit-booking-wizard"
          element={
            <ProtectedRoute>
              <MobileSimulator>
                <EditBookingWizard />
              </MobileSimulator>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-booking-wizard/:bookingId"
          element={
            <ProtectedRoute>
              <MobileSimulator>
                <EditBookingWizard />
              </MobileSimulator>
            </ProtectedRoute>
          }
        />
        <Route path="/direct-edit-booking/:bookingId" element={<DirectEditBooking />} />
        <Route path="/view-waivers/:bookingId" element={<ViewWaivers />} />
        <Route path="/edit-booking/:bookingId" element={<EditBooking />} />
        <Route path="/check-in/:bookingId" element={<CheckIn />} />
        <Route path="/all-media/:bookingId" element={<AllMedia />} />
        <Route path="/problem-reports/:bookingId" element={<ProblemReports />} />
        <Route path="/submit-bug-report" element={<SubmitBugReport />} />
        <Route path="/view-bug-reports" element={<ViewBugReports />} />
        <Route
          path="/bug-report"
          element={
            <MobileSimulator>
              <BugReport />
            </MobileSimulator>
          }
        />
        <Route path="/cancel-booking-confirmation/:bookingId" element={<CancelBookingConfirmation />} />
        <Route path="/reset-booking-pin" element={<ResetBookingPin />} />
        <Route path="/transfer-waiver-page/:bookingId" element={<TransferWaiverPage />} />

        {/* Auth route */}
        <Route path="/login" element={<Login />} />

        {/* Backward compatibility redirects for old routes */}
        <Route path="/mobile" element={<Navigate to="/today" replace />} />
        <Route path="/desktop" element={<Navigate to="/web-base" replace />} />
        <Route path="/booking/:bookingId" element={<BookingRedirect />} />
        <Route path="/cancel-booking/:bookingId" element={<CancelBookingRedirect />} />
        <Route path="/reset-pin" element={<Navigate to="/reset-booking-pin" replace />} />
        <Route path="/transfer-waiver/:bookingId" element={<TransferWaiverRedirect />} />

          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </ErrorBoundary>
  );
};

export default App;
